import { Search, X } from 'lucide-react';
import { memo, useEffect, useEffectEvent, useId, useMemo, useRef, useState } from 'react';
import { Picture, StoneSkeleton } from '@/components/media';
import { Button } from '@/components/ui/Button';
import type { CatalogFilterState } from '@/lib/catalog';
import { useAttributes, useProductPages } from '@/lib/catalog';
import { interpolate, useFormatters } from '@/lib/i18n';
import type { VisualizerCopy } from './content';
import { useDebounced } from './useDebounced';
import { PICKER_PAGE_SIZE } from './useStone';
import { useRovingRadio } from './useRovingRadio';

export interface StonePickerProps {
  /** Slug of the stone on screen (it may not be in the loaded results). */
  selected: string | null;
  /** Called with the stone's slug and its name in the language on screen. */
  onSelect: (slug: string, name: string) => void;
  /** One polite message at a time, through the page's single live region. */
  onAnnounce: (message: string) => void;
  copy: VisualizerCopy['stone'];
  announce: VisualizerCopy['announce'];
  /** Id of the heading that names the picker. */
  labelledBy: string;
}

/**
 * Search, colour-family chips and a scrollable grid of the collection's thumbnails with "Load more".
 * Selecting a stone only reports its slug — the page fetches its detail (and texture). The grid is one
 * radio group: a single tab stop, arrows move and select.
 */
export const StonePicker = memo(function StonePicker({
  selected,
  onSelect,
  onAnnounce,
  copy,
  announce,
  labelledBy,
}: StonePickerProps) {
  const { formatNumber } = useFormatters();
  const searchId = useId();
  const [text, setText] = useState('');
  const query = useDebounced(text.trim(), 300);
  const [colours, setColours] = useState<readonly string[]>([]);

  const filters = useMemo<CatalogFilterState>(
    () => ({
      sort: query ? 'relevance' : 'featured',
      ...(query ? { q: query } : {}),
      ...(colours.length > 0 ? { color: [...colours] } : {}),
    }),
    [query, colours],
  );
  const filterKey = `${query}|${colours.join(',')}`;

  const attributes = useAttributes();
  const families = attributes.data?.find((attribute) => attribute.key === 'color-family')?.values;

  const results = useProductPages(filters, { limit: PICKER_PAGE_SIZE });
  const items = useMemo(
    () => results.data?.pages.flatMap((page) => page.items) ?? [],
    [results.data],
  );
  const total = results.data?.pages[0]?.total ?? 0;
  const selectedIndex = items.findIndex((item) => item.slug === selected);
  const item = useRovingRadio<HTMLButtonElement>({
    count: items.length,
    selected: selectedIndex,
    onSelect: (index) => {
      const product = items[index];
      if (product) onSelect(product.slug, product.name);
    },
  });

  // One announcement per settled result set (not per keystroke): the first one is the page load.
  const announced = useRef<string | null>(null);
  const settled = results.isSuccess && !results.isPlaceholderData ? filterKey : null;
  const tell = useEffectEvent((message: string) => {
    onAnnounce(message);
  });
  useEffect(() => {
    if (settled === null || announced.current === settled) return;
    const first = announced.current === null;
    announced.current = settled;
    if (!first) {
      tell(
        items.length === 0
          ? copy.empty.title
          : interpolate(copy.showing, {
              shown: formatNumber(items.length),
              total: formatNumber(total),
            }),
      );
    }
  }, [settled, items.length, total, copy.empty.title, copy.showing, formatNumber]);

  // Bring a stone chosen elsewhere (a shared link, the default) into view — inside the grid only.
  const scroller = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const box = scroller.current;
    const current = box?.querySelector<HTMLElement>('[aria-checked="true"]');
    if (!box || !current) return;
    const top = current.offsetTop;
    const bottom = top + current.offsetHeight;
    if (top < box.scrollTop) box.scrollTop = Math.max(0, top - 8);
    else if (bottom > box.scrollTop + box.clientHeight)
      box.scrollTop = bottom - box.clientHeight + 8;
  }, [selected, items.length]);

  const loadMore = async (): Promise<void> => {
    const before = items.length;
    const next = await results.fetchNextPage();
    const after = next.data?.pages.reduce((sum, page) => sum + page.items.length, 0) ?? before;
    if (after > before)
      onAnnounce(interpolate(announce.more, { count: formatNumber(after - before) }));
  };

  const filtersActive = query !== '' || colours.length > 0;
  const loading = results.isPending;
  const failed = results.isError && items.length === 0;

  return (
    <div className="viz-stones">
      <div className="viz-search">
        <label className="sr-only" htmlFor={searchId}>
          {copy.searchLabel}
        </label>
        <Search className="viz-search__icon" size={18} aria-hidden="true" />
        <input
          id={searchId}
          className="field viz-search__input"
          type="search"
          autoComplete="off"
          spellCheck={false}
          enterKeyHint="search"
          maxLength={100}
          placeholder={copy.searchPlaceholder}
          value={text}
          onChange={(event) => {
            setText(event.target.value);
          }}
        />
        {text ? (
          <button
            type="button"
            className="viz-search__clear"
            aria-label={copy.clearSearch}
            onClick={() => {
              setText('');
            }}
          >
            <X size={16} aria-hidden="true" />
          </button>
        ) : null}
      </div>

      {families && families.length > 0 ? (
        <div className="viz-families" role="group" aria-label={copy.colourLabel}>
          <button
            type="button"
            className="viz-family"
            aria-pressed={colours.length === 0}
            onClick={() => {
              setColours([]);
            }}
          >
            {copy.allColours}
          </button>
          {families.map((family) => (
            <button
              key={family.slug}
              type="button"
              className="viz-family"
              aria-pressed={colours.includes(family.slug)}
              onClick={() => {
                setColours((current) => toggleList(current, family.slug));
              }}
            >
              {family.swatchHex ? (
                <span
                  className="viz-family__swatch"
                  style={{ backgroundColor: family.swatchHex }}
                  aria-hidden="true"
                />
              ) : null}
              {family.label}
            </button>
          ))}
        </div>
      ) : null}

      <div
        ref={scroller}
        className="viz-stone-scroll"
        data-dim={results.isPlaceholderData || undefined}
        aria-busy={loading || results.isFetching || undefined}
      >
        {loading ? (
          <div className="viz-stone-grid" aria-hidden="true">
            {Array.from({ length: 6 }, (_, index) => (
              <StoneSkeleton key={index} className="viz-stone-skeleton" />
            ))}
          </div>
        ) : failed ? (
          <div className="viz-note" data-tone="error">
            <p className="viz-note__title">{copy.error.title}</p>
            <p className="t-small text-fg-muted">{copy.error.body}</p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                void results.refetch();
              }}
            >
              {copy.error.retry}
            </Button>
          </div>
        ) : items.length === 0 ? (
          <div className="viz-note">
            <p className="viz-note__title">{copy.empty.title}</p>
            <p className="t-small text-fg-muted">{copy.empty.body}</p>
            {filtersActive ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setText('');
                  setColours([]);
                }}
              >
                {copy.empty.reset}
              </Button>
            ) : null}
          </div>
        ) : (
          <div className="viz-stone-grid" role="radiogroup" aria-labelledby={labelledBy}>
            {items.map((product, index) => {
              const colour = product.color ? product.color.label : '';
              return (
                <button key={product.id} type="button" className="viz-stone" {...item(index)}>
                  <span className="viz-stone__media">
                    {product.cover ? (
                      <Picture
                        image={product.cover}
                        alt=""
                        sizes="(min-width: 1024px) 130px, 30vw"
                        className="viz-stone__img"
                      />
                    ) : (
                      <StoneSkeleton tone="still" className="viz-stone__img" />
                    )}
                  </span>
                  <span className="viz-stone__name">{product.name}</span>
                  {colour ? <span className="viz-stone__colour">{colour}</span> : null}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {items.length > 0 ? (
        <div className="viz-stones__foot">
          <p className="t-small text-fg-muted">
            {interpolate(copy.showing, {
              shown: formatNumber(items.length),
              total: formatNumber(total),
            })}
          </p>
          {results.hasNextPage ? (
            <Button
              size="sm"
              variant="glass"
              loading={results.isFetchingNextPage}
              onClick={() => {
                void loadMore();
              }}
            >
              {copy.loadMore}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
});

/** Add or remove `value` from a list, keeping it immutable (the chips toggle). */
function toggleList(current: readonly string[], value: string): readonly string[] {
  return current.includes(value) ? current.filter((entry) => entry !== value) : [...current, value];
}
